import React from 'react';

import { maskAbhaAddress, maskAbhaNumber } from '../../../services/api/abhaProfile';
import { ABHA_NUMBER_LABEL, ABHA_ADDRESS_LABEL } from '../../../constants/abdmCopy';
import InvoiceSection from './InvoiceSection';

// The ABHA number and the ABHA address are separate patient keys (abha_number, abha_address).
// Records stored before the split can hold the address under abha_number, so an "@" value
// there is shown as the address. Both are shown masked, never in full.
function abhaIdentity(patient) {
  const number = patient?.abha_number || '';
  const addressInNumber = number.includes('@');
  return {
    number: addressInNumber ? '' : number,
    address: patient?.abha_address || (addressInNumber ? number : ''),
  };
}

/**
 * ClinicalRecordRenderer — shared renderer for every HI type whose parsedSummary comes
 * from bundle_parser.py's parse_bundle() (all eight: OPConsultation, Prescription,
 * DiagnosticReport, DischargeSummary, Invoice, ImmunizationRecord, HealthDocumentRecord
 * and WellnessRecord; an unclassified "Unknown" type falls back to GenericRenderer).
 * parse_bundle() extracts the same shape regardless of hi_type —
 * { patient, organization, practitioner, encounter, medications: [...], conditions: [...],
 *   invoices: [...] }
 * — so one readable, sectioned layout serves all of them rather than duplicating
 * near-identical renderer files per type. All fields are optional/nullable — an
 * external HIP's bundle may omit any of them. Attachments are rendered separately by
 * RecordDetailBody's AttachmentsSection.
 *
 * Props:
 *   parsedSummary {object}
 *   isDark        {boolean}
 */
function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

// Encounter date + time, always in IST (the tester's clock), whatever the bundle's offset.
function formatDateTimeIST(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  })} IST`;
}

const dash = (v) => (v === undefined || v === null || String(v).trim() === '' ? '—' : String(v));

// follow_up may arrive as a string or { date, text } depending on the bundle.
function followUpText(f) {
  if (!f) return '';
  if (typeof f === 'string') return f;
  return [f.text, f.date ? formatDate(f.date) : ''].filter(Boolean).join(' — ');
}

const fieldRow = { display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13 };

export default function ClinicalRecordRenderer({ parsedSummary, isDark }) {
  const {
    patient, practitioner, organization, encounter, follow_up: followUp,
    medications = [], conditions = [], invoices = [],
  } = parsedSummary || {};
  const textMuted = isDark ? '#9ca3af' : '#6b7280';
  const textMain = isDark ? '#f1f5f9' : '#111827';
  const border = `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`;
  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc';
  const sectionLabel = { fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', marginBottom: 6, color: textMuted };

  const followUpLine = followUpText(followUp ?? encounter?.follow_up);
  const abha = abhaIdentity(patient);
  const hasContent = Boolean(
    patient?.name || patient?.mobile || abha.number || abha.address
    || encounter?.chief_complaint || encounter?.period_start || encounter?.status
    || practitioner?.name || organization?.name || followUpLine
    || conditions.length || medications.length || invoices.length,
  );

  if (!hasContent) {
    return <p style={{ fontSize: 13, color: textMuted }}>No consultation details available on this record.</p>;
  }

  const row = (label, value, strong) => (
    <div style={fieldRow}>
      <span style={{ color: textMuted }}>{label}</span>
      <span style={{ color: textMain, fontWeight: strong ? 600 : 400, textAlign: 'right' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ background: cardBg, border, borderRadius: 8, padding: 12 }}>
        <div style={sectionLabel}>PATIENT</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: textMain }}>{dash(patient?.name)}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 16px', marginTop: 4, fontSize: 12, color: textMuted }}>
          {patient?.gender && patient.gender !== 'unknown' && <span>Gender: {patient.gender}</span>}
          {patient?.birth_date && <span>DOB: {formatDate(patient.birth_date)}</span>}
          {patient?.mobile && <span>Mobile: {patient.mobile}</span>}
          {abha.number && <span>{ABHA_NUMBER_LABEL} {maskAbhaNumber(abha.number)}</span>}
          {abha.address && <span>{ABHA_ADDRESS_LABEL} {maskAbhaAddress(abha.address)}</span>}
        </div>
      </div>

      <div>
        <div style={sectionLabel}>VISIT DETAILS</div>
        {row('Doctor', dash(practitioner?.name) + (practitioner?.name && practitioner.qualification ? ` (${practitioner.qualification})` : ''), true)}
        {row('Visit date & time', formatDateTimeIST(encounter?.period_start))}
        {row('Chief complaint', dash(encounter?.chief_complaint), true)}
        {encounter?.status && row('Status', <span style={{ textTransform: 'capitalize' }}>{encounter.status}</span>)}
        {organization?.name && row('Facility', organization.name, true)}
      </div>

      {conditions.length > 0 && (
        <div>
          <div style={sectionLabel}>DIAGNOSIS</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: textMain }}>
            {conditions.map((c, i) => (
              <li key={c.id || i}>{c.display || 'Unspecified condition'}</li>
            ))}
          </ul>
        </div>
      )}

      {medications.length > 0 && (
        <div>
          <div style={sectionLabel}>MEDICATIONS</div>
          <div style={{ borderTop: border }}>
            {medications.map((m, i) => (
              <div key={m.id || i} style={{ padding: '6px 0', borderBottom: border, fontSize: 13 }}>
                <div style={{ color: textMain, fontWeight: 600 }}>{m.drug_name || 'Unnamed medication'}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 16px', color: textMuted, fontSize: 12 }}>
                  {/* Only a structured dose is a "Dose"; the HIP's free text (dose + how often + route) is "Instructions". */}
                  {m.dose || !m.dosage
                    ? <span>Dose: {dash(m.dose)}</span>
                    : <span>Instructions: {m.dosage}</span>}
                  <span>Frequency: {dash(m.frequency)}</span>
                  <span>Duration: {dash(m.duration)}</span>
                  {m.status && <span style={{ textTransform: 'capitalize' }}>{m.status}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {followUpLine && (
        <div>
          <div style={sectionLabel}>FOLLOW-UP</div>
          <div style={{ fontSize: 13, color: textMain }}>{followUpLine}</div>
        </div>
      )}

      {invoices.length > 0 && (
        <InvoiceSection invoices={invoices} isDark={isDark} ui={{ textMain, textMuted, border, cardBg, sectionLabel }} />
      )}
    </div>
  );
}
